// src/themes/modern/menu/components/MenuItemList.tsx
import { motion } from 'framer-motion';
import React, { useState, useCallback } from 'react';
import type { Branch, MenuItem } from '~/types/new_types';
import { SharedMenuItemCard, ImageZoomModal } from '~/components/shared';
import { useShoppingCart } from '~/contexts/ShoppingCartContext';
import { useBranch } from '~/contexts/BranchContext';
import { getDirectAddPayload, getItemName, getItemDescription } from '~/utils/menuItemHelper';
import { saveItemCardsToSession } from '~/components/modal/MenuItemDetails/utils/sessionUtils';
import { useTranslation } from 'react-i18next';

interface MenuItemListProps {
	items: MenuItem[];
	theme: Branch['theme'];
	setSelectedItem?: React.Dispatch<React.SetStateAction<MenuItem | null>>;
}

const MenuItemList: React.FC<MenuItemListProps> = ({ items, theme, setSelectedItem }) => {
	const { addToCart, addToCartWithVariants, setTempQuantity, getTempQuantity } = useShoppingCart();
	const { branchData } = useBranch();
	const { i18n } = useTranslation();
	
	// Zoom modal state
	const [showImageModal, setShowImageModal] = useState(false);
	const [zoomedItem, setZoomedItem] = useState<MenuItem | null>(null);

	const handleZoom = useCallback((item: MenuItem) => {
		setZoomedItem(item);
		setShowImageModal(true);
	}, []);

	const handleAddToCart = useCallback((item: MenuItem) => {
		const directPayload = getDirectAddPayload(item);

		if (directPayload && branchData?.branch?.id) {
			// Build card data for session storage
			const cardId = `${item.id}-${Date.now()}`;
			const cardVariants = directPayload.variants.map((v) => ({
				name: v.optionName,
				children: [{
					name: v.variantName,
					price: v.variantPrice,
					quantity: v.quantity,
				}],
				quantity: v.quantity,
			}));

			const itemData = {
				itemId: item.id,
				itemName: getItemName(item, i18n.language),
				itemDescription: getItemDescription(item, i18n.language),
				itemImage: item.image_url || '',
				basePrice: item.discounted_price || item.price,
				totalQuantity: 1,
				cards: [{
					cardId,
					cardName: getItemName(item, i18n.language),
					quantity: 1,
					variants: cardVariants,
					isAiSuggestion: false,
				}],
				timestamp: Date.now(),
				isDraft: false,
			};

			saveItemCardsToSession(item.id, itemData);

			// Update tempQuantities for cart badge
			const currentQty = getTempQuantity(item.id);
			setTempQuantity(item.id, currentQty + 1, directPayload.variants);

			if (directPayload.variants.length > 0) {
				addToCartWithVariants(item, branchData.branch.id, 1, directPayload.variants);
			} else {
				addToCart(item, branchData.branch.id, 1);
			}
		} else {
			setSelectedItem?.(item);
		}
	}, [branchData, addToCart, addToCartWithVariants, setSelectedItem, i18n.language, setTempQuantity, getTempQuantity]);

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1
			}
		}
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				type: "spring" as const,
				stiffness: 100,
				damping: 15
			}
		}
	};

	return (
		<>
			<motion.div
				className="space-y-4"
				variants={containerVariants}
				initial="hidden"
				animate="visible"
			>
				{items.map((item, index) => (
					<motion.div
						key={item.id}
						variants={itemVariants}
						custom={index}
					>
						<SharedMenuItemCard
							item={item}
							onZoom={handleZoom}
							onAdd={handleAddToCart}
						/>
					</motion.div>
				))}
			</motion.div>

			{/* Image Zoom Modal */}
			<ImageZoomModal
				isOpen={showImageModal}
				onClose={() => setShowImageModal(false)}
				item={zoomedItem}
				theme={theme}
			/>
		</>
	);
};

export default MenuItemList;